import axios from "axios";
import { hostname } from "../utils";

// Cap on logo uploads, shared across the company forms that allow a logo replacement.
export const MAX_LOGO_BYTES = 2 * 1024 * 1024;

// Error thrown for a file the user can fix (wrong type / too large). Callers can surface
// `.message` directly; any other failure is an unexpected upload error.
export class LogoValidationError extends Error {}

const readAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Failed to read image file"));
    reader.readAsDataURL(file);
  });

const probeDimensions = (
  dataUrl: string
): Promise<{ width: number; height: number }> =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.onerror = () => resolve({ width: 0, height: 0 });
    img.src = dataUrl;
  });

// Validate, read, and POST a company logo to /platform-images, returning the re-hosted URL.
// Prefers publicUrl (respects whitelabel domains), falling back to url. Throws
// LogoValidationError for a user-fixable problem; other errors propagate as-is.
export async function uploadCompanyLogo(
  file: File,
  getToken: () => Promise<string>
): Promise<string> {
  if (!file.type.startsWith("image/")) {
    throw new LogoValidationError("Please upload an image file (PNG, JPG, etc.)");
  }
  if (file.size > MAX_LOGO_BYTES) {
    throw new LogoValidationError("Image size should be less than 2MB");
  }

  const token = await getToken();
  const base64String = await readAsDataUrl(file);
  const dimensions = await probeDimensions(base64String);

  const res = await axios.post(
    `${hostname}/platform-images`,
    {
      name: "Company Logo",
      category: "company-logo",
      url: base64String,
      mimeType: file.type,
      width: dimensions.width.toString(),
      height: dimensions.height.toString(),
      fileSize: file.size.toString(),
      public: true,
    },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return res.data?.publicUrl || res.data?.url || "";
}
